// O construtor visual da tela do PDV: biblioteca à esquerda, preview no meio,
// propriedades à direita.
import React, { useState } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/modals/ItemModal";
import { COMPONENT_TYPES } from "./catalogoDeComponentes";
import { PDVComponent } from "./tipos";
import { ComponenteNoPreview } from "./ComponenteNoPreview";
import { ItemDaBiblioteca } from "./ItemDaBiblioteca";
import { PropriedadesDoComponente } from "./PropriedadesDoComponente";
import { CamadaDePreviaDoArraste } from "./PreviaDoArraste";

export const ConstrutorVisualDoPdv: React.FC<{
  selectedSaleOption: any;
  localFormData: any;
  onUpdateComponent: (componentId: string, updates: any) => void;
  onAddComponent: (componentType: string) => void;
  editingComponent: PDVComponent | null;
  onSelectComponent: (component: PDVComponent) => void;
}> = ({
  selectedSaleOption,
  localFormData,
  onUpdateComponent,
  onAddComponent,
  editingComponent,
  onSelectComponent,
}) => {
  const [componentSearchTerm, setComponentSearchTerm] = useState("");

  const filteredComponents = COMPONENT_TYPES.filter(
    (component) =>
      component.name
        .toLowerCase()
        .includes(componentSearchTerm.toLowerCase()) ||
      component.type.toLowerCase().includes(componentSearchTerm.toLowerCase())
  );

  return (
    <AlfredoStyles.VisualBuilderContainer>
      <AlfredoStyles.ComponentLibrary>
        <AlfredoStyles.LibraryHeader>
          <AlfredoStyles.SectionTitle>
            Biblioteca de Componentes
          </AlfredoStyles.SectionTitle>
          <AlfredoStyles.SearchInput>
            <input
              type="text"
              placeholder="Buscar componentes..."
              value={componentSearchTerm}
              onChange={(e) => setComponentSearchTerm(e.target.value)}
            />
            <span className="material-icons">search</span>
          </AlfredoStyles.SearchInput>
        </AlfredoStyles.LibraryHeader>

        <AlfredoStyles.ComponentsList>
          {filteredComponents.map((component) => (
            <ItemDaBiblioteca
              key={component.id}
              componentType={component}
              onAddComponent={onAddComponent}
            />
          ))}
        </AlfredoStyles.ComponentsList>

        <AlfredoStyles.LibraryFooter>
          <AlfredoStyles.TipBox>
            <span className="material-icons">lightbulb</span>
            <div>
              <strong>Dica:</strong> Arraste componentes para a área de preview
              ou clique para adicionar automaticamente.
            </div>
          </AlfredoStyles.TipBox>
        </AlfredoStyles.LibraryFooter>
      </AlfredoStyles.ComponentLibrary>

      <AlfredoStyles.BuilderMain>
        <AlfredoStyles.PreviewContainer>
          <AlfredoStyles.PreviewHeader>
            <AlfredoStyles.PreviewTitle>
              Preview:{" "}
              {selectedSaleOption?.parsedValue.saleOptionName || "Tela Inicial"}
              <AlfredoStyles.PreviewBadge>
                {localFormData.viewType === "paisagem" ? "Paisagem" : "Retrato"}
              </AlfredoStyles.PreviewBadge>
            </AlfredoStyles.PreviewTitle>
            <AlfredoStyles.PreviewActions>
              <AlfredoStyles.ZoomControls>
                <button>
                  <span className="material-icons">remove</span>
                </button>
                <span>100%</span>
                <button>
                  <span className="material-icons">add</span>
                </button>
              </AlfredoStyles.ZoomControls>
            </AlfredoStyles.PreviewActions>
          </AlfredoStyles.PreviewHeader>

          <AlfredoStyles.InteractivePreview
            backgroundColor={
              selectedSaleOption?.parsedValue.saleOptionBackgroundColor ||
              localFormData.backgroundColor
            }
            viewType={localFormData.viewType as "retrato" | "paisagem"}
          >
            <AlfredoStyles.DropTarget>
              {selectedSaleOption?.parsedValue.saleOptionComponents?.map(
                (component: PDVComponent) => (
                  <ComponenteNoPreview
                    key={component.componentId}
                    component={component}
                    onSelect={onSelectComponent}
                    onUpdate={onUpdateComponent}
                    isSelected={
                      editingComponent?.componentId === component.componentId
                    }
                  />
                )
              )}

              {(selectedSaleOption?.parsedValue.saleOptionComponents?.length ===
                0 ||
                !selectedSaleOption?.parsedValue.saleOptionComponents) && (
                <AlfredoStyles.EmptyState>
                  <AlfredoStyles.EmptyStateIcon>
                    <span className="material-icons">widgets</span>
                  </AlfredoStyles.EmptyStateIcon>
                  <AlfredoStyles.EmptyStateTitle>
                    Nenhum componente adicionado
                  </AlfredoStyles.EmptyStateTitle>
                  <AlfredoStyles.EmptyStateDescription>
                    Arraste componentes da biblioteca ou clique para adicionar
                  </AlfredoStyles.EmptyStateDescription>
                  <AlfredoStyles.EmptyStateAction>
                    <button onClick={() => onAddComponent("Button")}>
                      <span className="material-icons">add</span>
                      Adicionar Primeiro Componente
                    </button>
                  </AlfredoStyles.EmptyStateAction>
                </AlfredoStyles.EmptyState>
              )}
            </AlfredoStyles.DropTarget>
          </AlfredoStyles.InteractivePreview>

          <AlfredoStyles.PreviewFooter>
            <AlfredoStyles.DeviceMockup>
              <AlfredoStyles.DeviceFrame>
                <AlfredoStyles.ScreenSize>
                  {localFormData.viewType === "paisagem"
                    ? "1024x768"
                    : "768x1024"}
                </AlfredoStyles.ScreenSize>
              </AlfredoStyles.DeviceFrame>
            </AlfredoStyles.DeviceMockup>
          </AlfredoStyles.PreviewFooter>
        </AlfredoStyles.PreviewContainer>
      </AlfredoStyles.BuilderMain>

      <AlfredoStyles.PropertiesPanel>
        <AlfredoStyles.PropertiesHeader>
          <AlfredoStyles.SectionTitle>
            {editingComponent
              ? "Propriedades do Componente"
              : "Propriedades da Tela"}
          </AlfredoStyles.SectionTitle>
        </AlfredoStyles.PropertiesHeader>

        <AlfredoStyles.PropertiesContent>
          {editingComponent ? (
            <PropriedadesDoComponente
              component={editingComponent}
              onUpdate={onUpdateComponent}
            />
          ) : (
            <AlfredoStyles.FieldsContainer>
              <AlfredoStyles.SectionTitle>
                Configurações Gerais
              </AlfredoStyles.SectionTitle>
              <AlfredoStyles.FormGrid>
                <AlfredoStyles.InputWrapper>
                  <label>Nome da Opção</label>
                  <AlfredoStyles.Input
                    type="text"
                    value={selectedSaleOption?.parsedValue.saleOptionName || ""}
                    onChange={() => {}}
                  />
                </AlfredoStyles.InputWrapper>
                <AlfredoStyles.InputWrapper>
                  <label>Cor de Fundo</label>
                  <AlfredoStyles.ColorInput
                    color={
                      selectedSaleOption?.parsedValue
                        .saleOptionBackgroundColor || "#FFFFFF"
                    }
                  >
                    <input
                      type="color"
                      value={
                        selectedSaleOption?.parsedValue
                          .saleOptionBackgroundColor || "#FFFFFF"
                      }
                      onChange={() => {}}
                    />
                    <label>Fundo</label>
                  </AlfredoStyles.ColorInput>
                </AlfredoStyles.InputWrapper>
              </AlfredoStyles.FormGrid>
            </AlfredoStyles.FieldsContainer>
          )}
        </AlfredoStyles.PropertiesContent>
      </AlfredoStyles.PropertiesPanel>

      <CamadaDePreviaDoArraste />
    </AlfredoStyles.VisualBuilderContainer>
  );
};
